import { useTranslation } from '@dagens/frontend-i18n';
import { capitalize } from '@dagensmat/core';
import { useEffect } from 'react';

import { Page } from '../../../components/page';
import { Order } from '../../../types/Order';
import { OnboardingHistoryKeys } from '../../../types/Producer';
import Callout from '../../../carrot-deprecated/callout';
import { ButtonAnchor } from '../../../_common/components/button/Button.style';
import Icon from '../../../_common/components/utils/Icon';
import {
  FeatureFlags,
  hasUserFeatureFlag
} from '../../../services/FeatureFlagService';
import OrdersPageHOC from './OrdersPageHOC';
import Theme from '@carrot-deprecated/theme';
import Container from '@carrot-deprecated/container';
import Typography from '@carrot-deprecated/typography';
import { hasAllOrdersLeftProducer, PRODUCER_DELIVERS } from 'utils/delivery';
import { formatDate, getDate, isTodayOrBefore } from 'utils/date/format';
import {
  countActiveUnopenedOrders,
  fetchOrders,
  groupOrdersByPickupType,
  splitActiveAndPastOrders
} from '_common/reducers/orders';
import { useAppDispatch, useAppSelector } from '_common/hooks/reduxHooks';
import Tag from '_common/components/utils/Tag';
import LinkListElement from '_common/components/link-list-element/LinkListElement';

const DeliveryStatusTag = ({
  orderGroup
}: {
  orderGroup: {
    producerDeliveryDate: string;
    orders: Order[];
  };
}) => {
  const { t } = useTranslation();
  const { producerDeliveryDate, orders } = orderGroup;
  if (
    isTodayOrBefore(producerDeliveryDate) &&
    !hasAllOrdersLeftProducer(orders)
  )
    return (
      <Tag bgColor={Theme.Colours.warning} text={t('producer:ActionNeeded')} />
    );

  const numActiveUnopenedOrders = countActiveUnopenedOrders(orders);
  if (numActiveUnopenedOrders) {
    return (
      <Tag
        bgColor={Theme.Colours.dagensPurple}
        text={t('common:NewOrder', {
          count: numActiveUnopenedOrders
        })}
      />
    );
  }

  return null;
};

type Props = {
  roleId: string | undefined;
  orderGroups: ReturnType<typeof groupOrdersByPickupType>;
  activeOrders: Order[];
  onboardingStep: {
    messageKey: string;
    isOnboarding: boolean;
  };
};

const OrdersPage = ({
  roleId,
  orderGroups = [],
  activeOrders,
  onboardingStep = {
    messageKey: 'producer:orders.receivedFirstOrder',
    isOnboarding: false
  }
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const features = useAppSelector(({ auth: { features } }) => features);

  useEffect(() => {
    dispatch(fetchOrders({ producerId: roleId }, { clearStore: false }));
  }, []);

  const thisRole = {
    'ds10.requestedid': roleId
  };
  const thisRoleParams = encodeURIComponent(JSON.stringify(thisRole));
  const reportUrlThis = `https://lookerstudio.google.com/reporting/faf9c30e-4015-429e-9d48-5be1f3a0e2d8/?params=${thisRoleParams}`;

  return (
    <>
      {onboardingStep.isOnboarding && (
        <Callout colour="noteworthy" mt={'s'}>
          <Typography variant="paragraph">
            {t(onboardingStep.messageKey)}
          </Typography>
          {hasUserFeatureFlag(FeatureFlags.DELIVERY_REPORT_BETA, features) && (
            <ButtonAnchor
              variant={'borderless'}
              href={reportUrlThis}
              mr={Theme.Spacings.s}
              target="_blank"
            >
              {t('producer:orders.betaReportDeliveryOverview')}{' '}
              <Icon icon="externalLinkSmall" />{' '}
            </ButtonAnchor>
          )}
        </Callout>
      )}
      {activeOrders.length === 0 && (
        <Typography variant="paragraph" color="secondary" my="m">
          {t('producer:NoActiveOrdersRightNow')}
        </Typography>
      )}
      {activeOrders.length > 0 && (
        <Container my="xl">
          {orderGroups.map(orderGroup => {
            const { key, type, orders, deliveryDate, producerDeliveryDate } =
              orderGroup;
            const header = capitalize(formatDate(deliveryDate));

            const deliveryTextKey =
              type === PRODUCER_DELIVERS
                ? 'common:SelfDelivery'
                : `${t('common:DagensDelivery')} (${t(
                    'producer:DropOff'
                  )} ${getDate(producerDeliveryDate)})`;
            const subTitle = `${t('common:numOrders', {
              count: orders.length
            })} → ${t(deliveryTextKey)}`;
            return (
              <LinkListElement
                key={key}
                header={header}
                subTitle={subTitle}
                to={`/orders/by-delivery/${key}`}
                state={{ returnPath: true }}
              >
                <DeliveryStatusTag orderGroup={orderGroup} />
              </LinkListElement>
            );
          })}
        </Container>
      )}
    </>
  );
};

const OrdersPageFetcher = () => {
  const { req, numUnopenedOrders, ...props } = useAppSelector(
    ({ auth: { _id: roleId, onboardingHistory }, orders: { items, req } }) => {
      const { activeOrders } = splitActiveAndPastOrders(items);
      const numUnopenedOrders = countActiveUnopenedOrders(items);
      const orderGroups = groupOrdersByPickupType(activeOrders);

      return {
        req,
        roleId,
        numUnopenedOrders,
        activeOrders,
        orderGroups,
        onboardingStep: {
          messageKey: 'producer:orders.receivedFirstOrder',
          isOnboarding: Boolean(
            onboardingHistory?.[OnboardingHistoryKeys.receivedFirstOrder]
          )
        }
      };
    }
  );

  if (req !== 'SUCCESS') {
    return <Page.Status req={req} />;
  }

  return (
    <OrdersPageHOC numUnopenedOrders={numUnopenedOrders}>
      <OrdersPage {...props} />
    </OrdersPageHOC>
  );
};

export default OrdersPageFetcher;
